import { giftWrapEventListener } from './giftWrapEventListener.js';
import { logError, logInfo } from '../utilities/logging.js';

// Throttling mechanism to prevent excessive cleanup calls
let lastCleanupTime = 0;
const CLEANUP_THROTTLE_MS = 30000; // 30 seconds

/**
 * Removes a dangling gift wrap key from a parent product.
 * @param {Object} parent - Parent product information
 * @param {Object} parent.item - The cart item
 * @param {string} parent.giftWrapKey - The gift wrap key to remove
 * @param {boolean} parent.hasPersonalisation - Whether the item has personalisation
 * @param {number} parent.lineIndex - The line index in the cart
 * @returns {Promise<void>}
 */
const removeDanglingGiftWrapKey = async (parent) => {
  const { item, hasPersonalisation, lineIndex } = parent;

  if (!item.properties) {
    return; // No properties to clean up
  }

  let updatedProperties = { ...item.properties };

  if (hasPersonalisation && updatedProperties._personalisation_base_attributes) {
    // Remove from _personalisation_base_attributes
    let baseAttributes = {};

    // Handle both string (JSON) and object formats
    if (typeof updatedProperties._personalisation_base_attributes === 'string') {
      try {
        baseAttributes = JSON.parse(updatedProperties._personalisation_base_attributes);
      } catch (error) {
        logError({
          label: 'removeDanglingGiftWrapKey',
          error,
          message: 'Failed to parse _personalisation_base_attributes string',
          extraData: {
            itemId: item.id,
            baseAttributes: updatedProperties._personalisation_base_attributes,
            baseAttributesType: typeof updatedProperties._personalisation_base_attributes
          }
        });
        return;
      }
    } else if (
      typeof updatedProperties._personalisation_base_attributes === 'object' &&
      updatedProperties._personalisation_base_attributes !== null
    ) {
      // If it's already an object, use it directly
      baseAttributes = { ...updatedProperties._personalisation_base_attributes };
    } else {
      logError({
        label: 'removeDanglingGiftWrapKey',
        error: new Error('Unexpected _personalisation_base_attributes type'),
        message: 'Unexpected type for _personalisation_base_attributes',
        extraData: {
          itemId: item.id,
          baseAttributes: updatedProperties._personalisation_base_attributes,
          baseAttributesType: typeof updatedProperties._personalisation_base_attributes
        }
      });
      return;
    }

    // Remove gift wrap related keys while preserving other attributes
    delete baseAttributes._giftwrap_key;
    // delete baseAttributes._vendorRoutingMatch;

    updatedProperties._personalisation_base_attributes = JSON.stringify(baseAttributes);
  } else {
    // Remove from regular properties
    delete updatedProperties._giftwrap_key;
    // delete updatedProperties._vendorRoutingMatch;
  }

  // Update the cart item using cart/change.js
  const payload = {
    line: lineIndex,
    properties: updatedProperties,
    sections: ['cart-drawer']
  };

  const response = await fetch('/cart/change.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    },
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    throw new Error(`Failed to update cart item: ${response.status} ${response.statusText}`);
  }

  const result = await response.json();

  // Update cart drawer silently if we have the HTML
  // @ts-expect-error - CartDrawer is added to window by cart-drawer.js
  if (result?.sections?.['cart-drawer'] && window.CartDrawer?.updateCart) {
    // @ts-expect-error - CartDrawer is added to window by cart-drawer.js
    await window.CartDrawer.updateCart({ html: result.sections['cart-drawer'] });
  }
};

/**
 * Checks for and removes orphaned gift wrap items and dangling gift wrap keys from the cart.
 * 1. Orphaned items: Gift wrap products that don't have a corresponding parent product
 * 2. Dangling keys: Parent products with gift wrap keys but no corresponding gift wrap items
 * @param {boolean} [force=false] - Force cleanup even if throttle time hasn't passed
 * @returns {Promise<boolean>} True if cleanup was performed successfully
 */
const cleanupOrphanedGiftWrapItems = async (force = false) => {
  try {
    // Skip cleanup if a gift wrap operation is in progress, unless forced.
    if (!force && typeof window !== 'undefined' && window.giftWrapService?.isOperationInProgress) {
      return true;
    }

    // Also skip if we are within the cooldown window after an operation.
    if (!force && typeof window !== 'undefined' && window.giftWrapService?.operationCooldownUntil) {
      if (Date.now() < window.giftWrapService.operationCooldownUntil) {
        return true;
      }
    }
    // Check throttling to prevent excessive API calls
    const now = Date.now();
    if (!force && now - lastCleanupTime < CLEANUP_THROTTLE_MS) {
      return true; // Skip cleanup, too soon since last run
    }

    // Get current cart data
    const cartData = await giftWrapEventListener.getCurrentCartData();

    if (!cartData?.items || !Array.isArray(cartData.items) || cartData.items.length === 0) {
      // No items in cart, nothing to clean up
      return true;
    }

    const orphanedGiftWrapItems = [];
    const parentProductsWithDanglingKeys = [];

    // Find gift wrap items that don't have a parent product
    for (const item of cartData.items) {
      const isGiftWrap = giftWrapEventListener.isGiftWrapItem(item);
      const hasParent = giftWrapEventListener.hasParentProduct(item, cartData.items);

      if (isGiftWrap && !hasParent) {
        orphanedGiftWrapItems.push(item);
      }
    }

    // Find parent products with dangling gift wrap keys (keys that have no corresponding gift wrap items)
    for (const item of cartData.items) {
      // Skip gift wrap items themselves
      if (giftWrapEventListener.isGiftWrapItem(item)) {
        continue;
      }

      // Extract gift wrap key from the item (check both locations)
      let giftWrapKey = null;
      let hasPersonalisation = false;

      // Check if item has personalisation indicators
      if (item.properties) {
        hasPersonalisation =
          item.properties._personalisation_choices ||
          item.properties._personalisation_id !== undefined ||
          item.properties._base === true ||
          item.properties._base === 'true' ||
          item.properties._personalisation_base_attributes;

        if (hasPersonalisation && item.properties._personalisation_base_attributes) {
          // Check in _personalisation_base_attributes
          // Handle both string (JSON) and object formats
          if (typeof item.properties._personalisation_base_attributes === 'string') {
            try {
              const baseAttributes = JSON.parse(item.properties._personalisation_base_attributes);
              giftWrapKey = baseAttributes._giftwrap_key;
            } catch {
              // If parsing fails, continue without error
            }
          } else if (
            typeof item.properties._personalisation_base_attributes === 'object' &&
            item.properties._personalisation_base_attributes !== null
          ) {
            // If it's already an object, access directly
            giftWrapKey = item.properties._personalisation_base_attributes._giftwrap_key;
          }
        } else if (!hasPersonalisation && item.properties._giftwrap_key) {
          // Check in regular properties for non-personalised items
          giftWrapKey = item.properties._giftwrap_key;
        }
      }

      // If we found a gift wrap key, check if there are any corresponding gift wrap items
      if (giftWrapKey) {
        const hasCorrespondingGiftWrapItems = cartData.items.some((cartItem) => {
          return giftWrapEventListener.isGiftWrapItem(cartItem) && cartItem.properties?._giftwrap_key === giftWrapKey;
        });

        if (!hasCorrespondingGiftWrapItems) {
          parentProductsWithDanglingKeys.push({
            item,
            giftWrapKey,
            hasPersonalisation,
            lineIndex: cartData.items.indexOf(item) + 1
          });
        }
      }
    }

    let cleanupPerformed = false;

    // Remove orphaned gift wrap items if any found
    if (orphanedGiftWrapItems.length > 0) {
      cleanupPerformed = true;

      // Extract all item keys for batch removal
      const itemKeys = orphanedGiftWrapItems.map((item) => item.key);

      // Remove all items in a single API call
      const result = await giftWrapEventListener.removeCartItems(itemKeys);

      // Update the cart drawer silently (without opening it)
      // @ts-expect-error - CartDrawer is added to window by cart-drawer.js
      if (result?.sections?.['cart-drawer'] && window.CartDrawer?.updateCart) {
        // @ts-expect-error - CartDrawer is added to window by cart-drawer.js
        await window.CartDrawer.updateCart({ html: result.sections['cart-drawer'] });
      }

      logInfo({
        label: 'cleanupOrphanedGiftWrapItems',
        info: `Successfully removed ${orphanedGiftWrapItems.length} orphaned gift wrap items`,
        message: 'Orphaned gift wrap cleanup completed',
        extraData: { removedItemsCount: orphanedGiftWrapItems.length }
      });

      // Update last cleanup time
      lastCleanupTime = now;
    }

    // Remove dangling gift wrap keys from parent products if any found
    if (parentProductsWithDanglingKeys.length > 0) {
      cleanupPerformed = true;

      // Process each parent product to remove the dangling gift wrap key
      for (const parent of parentProductsWithDanglingKeys) {
        try {
          await removeDanglingGiftWrapKey(parent);
        } catch (error) {
          logError({
            label: 'cleanupDanglingGiftWrapKeys',
            error,
            message: 'Failed to remove dangling gift wrap key from parent product',
            extraData: {
              itemId: parent.item.id,
              giftWrapKey: parent.giftWrapKey,
              hasPersonalisation: parent.hasPersonalisation
            }
          });
        }
      }
    }

    // Update last cleanup time if any cleanup was performed
    if (cleanupPerformed) {
      lastCleanupTime = now;
    }

    return true;
  } catch (error) {
    logError({
      label: 'cleanupOrphanedGiftWrapItems',
      error,
      message: 'Failed to cleanup orphaned gift wrap items',
      extraData: {}
    });
    return false;
  }
};

/**
 * Initializes the gift wrap event listener and performs cleanup.
 * @returns {Promise<boolean>} True if initialization was successful
 */
const initializeGiftWrap = async () => {
  try {
    // Check if gift wrap data is available.
    if (!window.giftWrapData?.enabled) {
      return false;
    }

    // Enable the gift wrap event listener.
    giftWrapEventListener.enable();

    // Set up global reference for backward compatibility.
    // @ts-expect-error - Custom window property
    window.giftWrapService = giftWrapEventListener;

    // Set up global cleanup function for manual use
    // @ts-expect-error - Custom window property
    window.cleanupOrphanedGiftWrap = () => cleanupOrphanedGiftWrapItems(true);

    // Perform orphaned gift wrap cleanup on page load
    await cleanupOrphanedGiftWrapItems();

    return true;
  } catch (error) {
    logError({
      label: 'initializeGiftWrap',
      error,
      message: 'Failed to initialize gift wrap event listener',
      extraData: {}
    });
    return false;
  }
};

/**
 * Auto-initializes the gift wrap event listener when the script loads.
 * @returns {Promise<void>}
 */
const autoInitialize = async () => {
  try {
    // Use a small delay to ensure other scripts have loaded.
    setTimeout(async () => {
      await initializeGiftWrap();
    }, 100);
  } catch (error) {
    logError({
      label: 'autoInitialize',
      error,
      message: 'Failed to auto-initialize gift wrap event listener',
      extraData: {}
    });
  }
};

// Auto-initialize when the script loads.
if (typeof window !== 'undefined') {
  autoInitialize();
}

export { initializeGiftWrap, giftWrapEventListener, cleanupOrphanedGiftWrapItems };
